import { useMemo } from 'react';
import type { ClassroomTopMoment, ClassroomTopMomentsConfig } from '@/types/classrooms';
import type { TopMomentsSectionProps } from './TopMomentsSection.types';

const MAX_SLOTS_OPTIONS: Array<3 | 5 | 7 | 10> = [3, 5, 7, 10];

export function TopMomentsSection({
  mode,
  value,
  availablePhotos = [],
  onChange,
}: TopMomentsSectionProps) {
  const config: ClassroomTopMomentsConfig = useMemo(
    () =>
      value ?? {
        maxSlots: 5,
        items: [],
      },
    [value]
  );

  if (mode === 'view') {
    if (!config.items.length) return null;

    return (
      <div className="grid gap-4 md:grid-cols-2">
        {config.items
          .slice(0, config.maxSlots)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .map((m) => {
            const photo = m.mediaRef ? availablePhotos.find((p) => p.id === m.mediaRef!.id) : null;

            return (
              <article key={m.id} className="overflow-hidden rounded-lg border bg-white">
                {photo && (
                  <div className="aspect-video w-full overflow-hidden bg-gray-100">
                    <img src={photo.url} alt={photo.caption ?? m.title} className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="space-y-1 p-3">
                  <h3 className="text-sm font-medium">{m.title}</h3>
                  {m.date && <p className="text-[11px] text-gray-500">{m.date}</p>}
                  {m.summary && <p className="text-xs text-gray-700 line-clamp-3">{m.summary}</p>}
                </div>
              </article>
            );
          })}
      </div>
    );
  }

  const handleUpdateItem = (idx: number, patch: Partial<ClassroomTopMoment>) => {
    if (!onChange) return;
    const items = [...config.items];
    items[idx] = { ...items[idx], ...patch } as ClassroomTopMoment;
    onChange({ ...config, items });
  };

  const handleAddMoment = () => {
    if (!onChange) return;
    if (config.items.length >= config.maxSlots) return;
    const id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `moment-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const items: ClassroomTopMoment[] = [
      ...config.items,
      {
        id,
        title: '',
        position: config.items.length + 1,
      },
    ];
    onChange({ ...config, items });
  };

  const handleRemoveMoment = (idx: number) => {
    if (!onChange) return;
    const items = [...config.items];
    items.splice(idx, 1);
    onChange({ ...config, items });
  };

  const handleReorder = (idx: number, direction: -1 | 1) => {
    if (!onChange) return;
    const items = [...config.items];
    const newIndex = idx + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const [moved] = items.splice(idx, 1);
    items.splice(newIndex, 0, moved);
    onChange({ ...config, items });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-gray-600">Number of moments:</span>
        {MAX_SLOTS_OPTIONS.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`rounded-full border px-2 py-1 text-xs ${
              config.maxSlots === opt ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 text-gray-700'
            }`}
            onClick={() =>
              onChange &&
              onChange({
                ...config,
                maxSlots: opt,
                items: config.items.slice(0, opt),
              })
            }
          >
            {opt}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {config.items.map((m, idx) => (
          <div key={m.id} className="flex flex-col gap-2 rounded-md border bg-gray-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-500">
                Moment {idx + 1} of {config.maxSlots}
              </span>
              <div className="flex items-center gap-1">
                <button type="button" className="text-[11px] text-gray-500" onClick={() => handleReorder(idx, -1)}>
                  ↑
                </button>
                <button type="button" className="text-[11px] text-gray-500" onClick={() => handleReorder(idx, 1)}>
                  ↓
                </button>
                <button type="button" className="text-[11px] text-red-500" onClick={() => handleRemoveMoment(idx)}>
                  Remove
                </button>
              </div>
            </div>

            <input
              className="w-full rounded-md border px-2 py-1 text-sm"
              placeholder="Title (e.g. Field trip to the museum)"
              value={m.title}
              onChange={(e) => handleUpdateItem(idx, { title: e.target.value })}
            />

            <div className="flex gap-2">
              <input
                type="date"
                className="rounded-md border px-2 py-1 text-xs"
                value={m.date ?? ''}
                onChange={(e) => handleUpdateItem(idx, { date: e.target.value || undefined })}
              />
            </div>

            <textarea
              className="w-full rounded-md border px-2 py-1 text-xs"
              rows={2}
              placeholder="Short description of this moment"
              value={m.summary ?? ''}
              onChange={(e) => handleUpdateItem(idx, { summary: e.target.value })}
            />

            {availablePhotos.length > 0 && (
              <div className="flex flex-col gap-1">
                <label className="text-[11px] text-gray-600">Attach photo (optional)</label>
                <select
                  className="rounded-md border px-2 py-1 text-xs"
                  value={m.mediaRef?.id ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    handleUpdateItem(idx, {
                      mediaRef: val ? { type: 'teacher_photo', id: val } : undefined,
                    });
                  }}
                >
                  <option value="">No photo</option>
                  {availablePhotos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.caption || p.id}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        ))}

        {config.items.length < config.maxSlots && (
          <button type="button" className="text-xs text-blue-600" onClick={handleAddMoment}>
            + Add moment
          </button>
        )}
      </div>
    </div>
  );
}
